import React, { useEffect, useRef } from 'react';
import { ResponsiveModal, ResponsiveModalContent } from "@/components/ui/responsive-modal";
import { PortableText } from '@portabletext/react';
import { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';

// Define the block type for Portable Text
interface PortableTextBlock {
  _type: 'block';
  children: Array<{
    text: string;
    _type: 'span';
  }>;
}

interface AuthorBioModalProps {
  isOpen: boolean;
  onClose: () => void;
  author: {
    name: string;
    bio?: string | PortableTextBlock | PortableTextBlock[];
    image?: { asset: { url: string, _ref?: string } };
  };
  urlFor: (source: any) => ImageUrlBuilder;
}

export const AuthorBioModal: React.FC<AuthorBioModalProps> = ({
  isOpen,
  onClose,
  author,
  urlFor
}) => {
  const onCloseRef = useRef(onClose);

  // Keep the ref updated with the latest onClose callback
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;
    const onPopState = (e: PopStateEvent) => {
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    window.history.pushState(null, '', window.location.href);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen]);

  // Helper function to get image URL
  const getImageUrl = (source: any): string => {
    return urlFor(source).auto('format').url();
  };

  // Function to parse HTML links in bio string
  const parseBioWithLinks = (bio: string): React.ReactNode => {
    const linkRegex = /<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let linkCount = 0;
    let match;

    while ((match = linkRegex.exec(bio)) !== null && linkCount < 3) {
      if (match.index > lastIndex) {
        parts.push(bio.substring(lastIndex, match.index));
      }
      parts.push(
        <a
          key={`link-${linkCount}`}
          href={match[1]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline font-semibold"
        >
          {match[2]}
        </a>
      );
      lastIndex = match.index + match[0].length;
      linkCount++;
    }
    if (lastIndex < bio.length) {
      parts.push(bio.substring(lastIndex));
    }
    return <>{parts}</>;
  };

  // Function to render bio content based on its type
  const renderBio = () => {
    if (!author.bio) return null;

    // If bio is a string, split it into paragraphs and parse links
    if (typeof author.bio === 'string') {
      return author.bio.split('\n').map((paragraph, index) => (
        <p key={index} className="text-foreground mb-4 last:mb-0">
          {parseBioWithLinks(paragraph)}
        </p>
      ));
    }

    // If bio is an array (Portable Text blocks)
    if (Array.isArray(author.bio)) {
      return (
        <PortableText 
          value={author.bio}
          components={{
            block: {
              normal: ({children}) => (
                <p className="text-foreground mb-4 last:mb-0">{children}</p>
              )
            }
          }}
        />
      );
    }

    // If bio is an object (single block)
    if (typeof author.bio === 'object' && 'children' in author.bio && author.bio._type === 'block') {
      return (
        <PortableText 
          value={[author.bio]}
          components={{
            block: {
              normal: ({children}) => (
                <p className="text-foreground mb-4 last:mb-0">{children}</p>
              )
            }
          }}
        />
      );
    }

    // Fallback for other types
    return <p className="text-foreground">{String(author.bio)}</p>;
  };

  return (
    <ResponsiveModal open={isOpen} setOpen={onClose} direction="top">
      <ResponsiveModalContent className="bg-background text-foreground w-full max-w-lg rounded-2xl p-0">
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex items-center gap-4">
            {author.image?.asset && (
              <img
                src={author.image.asset._ref ? getImageUrl(author.image) : author.image.asset.url}
                alt={author.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            )}
            <h2 className="text-2xl font-bold text-foreground">{author.name}</h2>
          </div>
          <div className="prose prose-lg mt-4 prose-foreground">
            {renderBio()}
          </div>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}; 